import { Immutable } from "immer";
import React from "react";
import { ProductReviewsSSR } from "tsi-common-react/src/apps/reviews/ProductReviewsSSR/index";
import { PromoConditionalBlock } from "tsi-common-react/src/components/PromoConditionalBlock/index";

import { templates } from "@reactivated";
import type { PillowsPromoConditionalBlock } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { CarouselModule } from "../../blocks/CarouselModule";
import { CustomerReviewsBlock } from "../../blocks/CustomerReviewsBlock";
import { FinePrintBarBlock } from "../../blocks/FinePrintBarBlock";
import { MoreInfoBlock } from "../../blocks/MoreInfoBlock";
import { MultiTabInfoBlock } from "../../blocks/MultiTabInfoBlock";
import { ProductDetailsBlock } from "../../blocks/ProductDetailsBlock";
import {
    pillowsPromoConditionalInnerMap,
    toolboxComponentsMap,
} from "../../blocks/toolboxComponentsMap";

type IProps = Immutable<{
    page: templates.ProductDisplayPageTemplate["page"];
    reviews: templates.ProductDisplayPageTemplate["reviews"];
}>;

/**
 * Component map for the pillows ProductDisplayPage body, minus ``reviews_full``
 * (which the Template adds inline because it depends on the page-level
 * ``props.reviews`` side channel). Exported so the block-screenshot styleguide
 * harness renders through the same map production uses.
 */
export const productDisplayPageBodyComponentsMap = {
    ...toolboxComponentsMap,
    promo_conditional_block: (block: {
        value: Immutable<PillowsPromoConditionalBlock>;
    }) => (
        <PromoConditionalBlock value={block.value}>
            <StreamField
                tagName={React.Fragment}
                value={block.value.content ?? []}
                components={pillowsPromoConditionalInnerMap}
            />
        </PromoConditionalBlock>
    ),
    carousel: CarouselModule,
    product_details_block: ProductDetailsBlock,
    multi_tab_info_block: MultiTabInfoBlock,
    fine_print_bar: FinePrintBarBlock,
    more_info: MoreInfoBlock,
    customer_reviews: CustomerReviewsBlock,
} as const;

/**
 * Pillows ProductDisplayPage body assembly: dispatches every block type in
 * ``ProductDisplayPageContent`` to its corresponding React component,
 * collapsing the N legacy ``{% include_block %}`` hydration roots into a
 * single React render tree (#33597).
 *
 * The ``reviews_full`` block renders the structure from the legacy
 * ``cms/components/pdp-reviews-full.html`` template: the outer
 * ``.pdp-section`` wrapper always renders, and the ``#reviews`` SSR reviews
 * component + CTA only render when the page-level reviews data
 * (``props.reviews``, computed by ``get_pdp_reviews_props``) is non-null —
 * mirroring the legacy ``{% if page.reviews_brand_id %}`` gate.
 */
export const ProductDisplayPageBody = (
    props: IProps,
): React.JSX.Element | null => {
    if (!props.page.pdp_props) {
        return null;
    }
    return (
        <StreamField
            tagName={React.Fragment}
            value={props.page.pdp_props.value}
            components={{
                ...productDisplayPageBodyComponentsMap,
                reviews_full: (block) => (
                    <div className="pdp-section">
                        {props.reviews && (
                            <>
                                <div id="reviews">
                                    <ProductReviewsSSR
                                        {...props.reviews}
                                        isCollapsedView={true}
                                    />
                                </div>
                                {block.value.show_cta && (
                                    <div className="pdp-reviews-full__cta">
                                        <a
                                            className="button button--ghost pdp-reviews-full__cta-link"
                                            href={block.value.cta?.url ?? ""}
                                        >
                                            {block.value.cta_text}
                                        </a>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                ),
            }}
        />
    );
};
